/**
 * Presentation helpers for a plan item: status chip, gate dots and the one-line
 * "why is it stuck" text — all built on the shared readiness engine.
 */
import type { PlanItemDTO } from '@clear/contracts';
import {
  blocker,
  GATES,
  isQualityUnlocked,
  itemStatus,
  needsAction,
  readyQty,
  type Gate,
} from '@clear/domain';
import { formatNumber, type Translate } from '@clear/i18n';
import { Chip, GateDots, ModelThumb, Swatch, type GateDot } from '@clear/ui';
import type { Lookups } from '../data/bootstrap';
import { useI18n } from '../i18n';
import { age, hhmm } from '../lib/time';

export function gateDots(item: PlanItemDTO, t: Translate, lk: Lookups): GateDot[] {
  return GATES.map((g) => {
    const e = item.gates[g];
    const name = t(`gates.${g}`);
    let kind: GateDot['kind'] = 'pend';
    let title = `${name}: ${t('gateStatus.pending')}`;
    if (!e && g === 'quality' && !isQualityUnlocked(item)) {
      kind = 'lock';
      title = `${name}: ${t('gateStatus.locked')}`;
    } else if (e) {
      if (e.status === 'ok' && !needsAction(item, g)) {
        kind = 'ok';
        title = `${name}: ${t('gateStatus.ok')}`;
      } else if (e.status === 'blocked') {
        kind = 'bad';
        title = `${name}: ${t(`gateStatus.blocked.${g}`)} · ${lk.reasonLabel(e.reasonId)}`;
      } else {
        kind = g === 'quality' && e.status === 'partial' ? 'bad' : 'part';
        title = `${name}: ${e.status === 'ok' ? t('floor.plusAdded', { n: formatNumber(item.qty - e.okQty) }) : t(`gateStatus.partial.${g}`)}`;
      }
    }
    return { key: g, letter: t(`gates.short.${g}`), kind, title };
  });
}

export function ItemDots({ item, lk, small }: { item: PlanItemDTO; lk: Lookups; small?: boolean }) {
  const { t } = useI18n();
  return <GateDots dots={gateDots(item, t, lk)} small={small} />;
}

export function StatusChip({ item }: { item: PlanItemDTO }) {
  const { t } = useI18n();
  if (item.cancelled) return <Chip tone="CANCELLED">{t('status.CANCELLED')}</Chip>;
  const s = itemStatus(item);
  return (
    <Chip tone={s}>
      {t(`status.${s}`)}
      {s === 'PART' ? ` ${formatNumber(readyQty(item))}` : ''}
    </Chip>
  );
}

export function whyText(item: PlanItemDTO, t: Translate, lk: Lookups): string {
  const s = itemStatus(item);
  if (s === 'STARTED') return t('dashboard.whyStarted', { time: hhmm(item.startedAt, lk.tz) });
  const b = blocker(item);
  if (!b) return '—';
  const gate = t(`gates.${b.gate}`);
  if (b.kind === 'hold') {
    return t('dashboard.whyHold', {
      gate,
      reason: lk.reasonLabel(b.reasonId) || t(`gateStatus.blocked.${b.gate}`),
      qty: formatNumber(b.qty),
      age: age(b.since),
    });
  }
  if (b.kind === 'partial')
    return t('dashboard.whyPartial', { gate, qty: formatNumber(b.qty), age: age(b.since) });
  return t('dashboard.whyPending', { gate, age: age(b.since) });
}

export function ModelMeta({ modelId, lk }: { modelId: string; lk: Lookups }) {
  const m = lk.model(modelId);
  return (
    <>
      <Swatch hex={m.colourHex} />
      {m.finish} · {m.sweepMm} mm
    </>
  );
}

export function Thumb({ modelId, lk, size }: { modelId: string; lk: Lookups; size?: number }) {
  const m = lk.model(modelId);
  return <ModelThumb hex={m.colourHex} blades={m.blades} photoUrl={m.photoUrl} size={size ?? 52} />;
}

export const gateLabelFor = (t: Translate, g: Gate, status: 'ok' | 'partial' | 'blocked') =>
  status === 'ok'
    ? t('gateStatus.ok')
    : status === 'partial'
      ? t(`gateStatus.partial.${g}`)
      : t(`gateStatus.blocked.${g}`);
